import { orders, type Order, managers as allManagers, clients as allClients } from "@/mocks/orders";
import { inventory } from "@/mocks/inventory";

export type UserRole = "manager" | "director";

interface QueryContext {
  role: UserRole;
  managerName?: string;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function filterOrdersByRole(os: Order[], ctx: QueryContext): Order[] {
  if (ctx.role === "director") return os;
  return os.filter((o) => o.manager === ctx.managerName);
}

async function handleOrderSummary(ctx: QueryContext): Promise<string> {
  const filtered = filterOrdersByRole(orders, ctx);
  const totalAmount = filtered.reduce((s, o) => s + o.amount, 0);
  const totalMargin = filtered.reduce((s, o) => s + o.margin, 0);
  const count = filtered.length;
  const avgCheck = count > 0 ? totalAmount / count : 0;

  const statusCounts: Record<string, number> = {};
  filtered.forEach((o) => {
    statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
  });

  const scope = ctx.role === "director" ? "по всем менеджерам" : "по Вашим заказам";

  let response = `📊 Сводка по заказам ${scope}:\n\n`;
  response += `• Всего заказов: ${count}\n`;
  response += `• Общая сумма: ${formatMoney(totalAmount)}\n`;
  response += `• Общая маржа: ${formatMoney(totalMargin)}\n`;
  response += `• Средний чек: ${formatMoney(avgCheck)}\n\n`;
  response += `Распределение по статусам:\n`;
  Object.entries(statusCounts).forEach(([status, cnt]) => {
    response += `  ▸ ${status}: ${cnt} заказ(ов)\n`;
  });

  return response;
}

async function handleTopItems(ctx: QueryContext): Promise<string> {
  const filtered = filterOrdersByRole(orders, ctx);
  const itemMap = new Map<string, { quantity: number; sum: number }>();

  filtered.forEach((o) => {
    o.items.forEach((item) => {
      const existing = itemMap.get(item.nomenclature) || { quantity: 0, sum: 0 };
      existing.quantity += item.quantity;
      existing.sum += item.sum;
      itemMap.set(item.nomenclature, existing);
    });
  });

  const sorted = Array.from(itemMap.entries())
    .sort((a, b) => b[1].sum - a[1].sum)
    .slice(0, 5);

  const scope = ctx.role === "director" ? "по всем менеджерам" : "по Вашим заказам";

  let response = `🏆 Топ-5 позиций по выручке ${scope}:\n\n`;
  sorted.forEach(([name, data], i) => {
    response += `${i + 1}. ${name}\n`;
    response += `   Выручка: ${formatMoney(data.sum)} | Продано: ${data.quantity} ед.\n\n`;
  });

  return response;
}

async function handleTopClients(ctx: QueryContext): Promise<string> {
  const filtered = filterOrdersByRole(orders, ctx);
  const clientMap = new Map<string, { amount: number; count: number }>();

  filtered.forEach((o) => {
    const existing = clientMap.get(o.client) || { amount: 0, count: 0 };
    existing.amount += o.amount;
    existing.count += 1;
    clientMap.set(o.client, existing);
  });

  const sorted = Array.from(clientMap.entries())
    .sort((a, b) => b[1].amount - a[1].amount)
    .slice(0, 5);

  const scope = ctx.role === "director" ? "по всем менеджерам" : "по Вашим заказам";

  let response = `👥 Топ-5 клиентов по выручке ${scope}:\n\n`;
  sorted.forEach(([name, data], i) => {
    response += `${i + 1}. ${name}\n`;
    response += `   Выручка: ${formatMoney(data.amount)} | Заказов: ${data.count}\n\n`;
  });

  return response;
}

async function handleTopManagers(ctx: QueryContext): Promise<string> {
  if (ctx.role === "manager") {
    return "У Вас нет прав для просмотра статистики по другим менеджерам. Этот отчёт доступен только руководителю.";
  }

  const mgrMap = new Map<string, { amount: number; count: number; margin: number }>();

  orders.forEach((o) => {
    const existing = mgrMap.get(o.manager) || { amount: 0, count: 0, margin: 0 };
    existing.amount += o.amount;
    existing.count += 1;
    existing.margin += o.margin;
    mgrMap.set(o.manager, existing);
  });

  const sorted = Array.from(mgrMap.entries()).sort((a, b) => b[1].amount - a[1].amount);

  let response = `👔 Статистика по менеджерам:\n\n`;
  sorted.forEach(([name, data], i) => {
    response += `${i + 1}. ${name}\n`;
    response += `   Выручка: ${formatMoney(data.amount)} | Заказов: ${data.count} | Маржа: ${formatMoney(data.margin)}\n\n`;
  });

  return response;
}

async function handleInventory(query: string, ctx: QueryContext): Promise<string> {
  const lower = query.toLowerCase();
  let items = inventory;

  if (lower.includes("остатк") || lower.includes("склад")) {
    const keyword = lower
      .replace(/остатк[и]?\s*(по|на)?\s*/i, "")
      .replace(/что\s+(на|по)\s*/i, "")
      .replace(/склад[е]?\s*/i, "")
      .trim();

    if (keyword.length > 2) {
      items = items.filter(
        (i) =>
          i.nomenclature.toLowerCase().includes(keyword) ||
          i.category.toLowerCase().includes(keyword)
      );
    }
  }

  let response = `📦 Остатки на складах:\n\n`;
  items.forEach((item) => {
    const available = item.quantity - item.reserved;
    const warning = available < item.minStock ? " ⚠️ НИЖЕ МИНИМУМА" : "";
    response += `▸ ${item.nomenclature}\n`;
    response += `  Склад: ${item.warehouse}\n`;
    response += `  Всего: ${item.quantity} ${item.unit} | Резерв: ${item.reserved} | Доступно: ${available} ${item.unit}${warning}\n\n`;
  });

  return response;
}

async function handleOverdueOrders(ctx: QueryContext): Promise<string> {
  const today = new Date("2026-06-22");
  const filtered = filterOrdersByRole(orders, ctx).filter((o) => {
    return o.status === "Просрочен" || (new Date(o.shipmentDate) < today && o.status !== "Отгружен");
  });

  if (filtered.length === 0) {
    return "✅ Просроченных или зависших заказов не обнаружено. Все заказы обрабатываются в срок.";
  }

  const scope = ctx.role === "director" ? "по компании" : "по Вашим заказам";
  let response = `🚨 Просроченные и зависшие заказы ${scope}:\n\n`;

  filtered.forEach((o) => {
    response += `▸ ${o.id} от ${formatDate(o.date)}\n`;
    response += `  Клиент: ${o.client}\n`;
    response += `  Сумма: ${formatMoney(o.amount)}\n`;
    response += `  Статус: ${o.status} | План отгрузки: ${formatDate(o.shipmentDate)}\n`;
    response += `  Менеджер: ${o.manager}\n\n`;
  });

  return response;
}

async function handleManagerOrders(query: string, ctx: QueryContext): Promise<string> {
  if (ctx.role === "manager") {
    return "В режиме менеджера Вы видите только свои заказы. Для просмотра заказов других менеджеров переключитесь на роль руководителя.";
  }

  const lower = query.toLowerCase();
  const targetManager = allManagers.find((m) => lower.includes(m.toLowerCase()));

  if (!targetManager) {
    return "Пожалуйста, уточните фамилию менеджера. Доступные: " + allManagers.join(", ");
  }

  const mgrOrders = orders.filter((o) => o.manager === targetManager);
  if (mgrOrders.length === 0) {
    return `У менеджера ${targetManager} нет заказов за текущий период.`;
  }

  let response = `📋 Заказы менеджера ${targetManager}:\n\n`;
  mgrOrders.forEach((o) => {
    response += `▸ ${o.id} | ${formatDate(o.date)} | ${o.client}\n`;
    response += `  Сумма: ${formatMoney(o.amount)} | Маржа: ${formatMoney(o.margin)} | Статус: ${o.status}\n\n`;
  });

  return response;
}

async function handleClientInfo(query: string, ctx: QueryContext): Promise<string> {
  const lower = query.toLowerCase();
  const targetClient = allClients.find((c) => lower.includes(c.toLowerCase()));

  if (!targetClient) {
    return "Пожалуйста, уточните название клиента. Доступные: " + allClients.join(", ");
  }

  const filtered = filterOrdersByRole(orders, ctx).filter((o) => o.client === targetClient);

  if (filtered.length === 0) {
    return `У клиента ${targetClient} нет заказов в рамках Вашего доступа за текущий период.`;
  }

  const totalAmount = filtered.reduce((s, o) => s + o.amount, 0);
  const totalMargin = filtered.reduce((s, o) => s + o.margin, 0);

  let response = `🏢 ${targetClient}\n\n`;
  response += `• Заказов за период: ${filtered.length}\n`;
  response += `• Общая выручка: ${formatMoney(totalAmount)}\n`;
  response += `• Общая маржа: ${formatMoney(totalMargin)}\n\n`;
  response += `Заказы:\n`;

  filtered.forEach((o) => {
    response += `▸ ${o.id} от ${formatDate(o.date)} | ${formatMoney(o.amount)} | ${o.status}\n`;
  });

  return response;
}

async function handleRecentOrders(query: string, ctx: QueryContext): Promise<string> {
  const lower = query.toLowerCase();
  const daysMatch = lower.match(/последни[ех]\s+(\d+)\s*(дн[яей]+|день)/);
  let days = 2;

  if (daysMatch) {
    const parsed = parseInt(daysMatch[1], 10);
    if (!isNaN(parsed) && parsed > 0 && parsed <= 365) {
      days = parsed;
    }
  }

  const today = new Date("2026-06-22");
  const sinceDate = new Date(today);
  sinceDate.setDate(sinceDate.getDate() - days);

  const filtered = filterOrdersByRole(orders, ctx).filter((o) => {
    const orderDate = new Date(o.date);
    return orderDate >= sinceDate && orderDate <= today;
  });

  const scope = ctx.role === "director" ? "по всем менеджерам" : "по Вашим заказам";

  if (filtered.length === 0) {
    const dayWord = days === 1 ? "день" : days >= 2 && days <= 4 ? "дня" : "дней";
    return `📅 За последни${days === 1 ? "й" : "е"} ${days} ${dayWord} ${scope} заказов не найдено.`;
  }

  const totalAmount = filtered.reduce((s, o) => s + o.amount, 0);
  const totalMargin = filtered.reduce((s, o) => s + o.margin, 0);
  const avgMarginPercent = totalAmount > 0 ? (totalMargin / totalAmount) * 100 : 0;

  const byDate: Record<string, Order[]> = {};
  filtered.forEach((o) => {
    if (!byDate[o.date]) byDate[o.date] = [];
    byDate[o.date].push(o);
  });

  const dayWord = days === 1 ? "день" : days >= 2 && days <= 4 ? "дня" : "дней";
  let response = `📅 Заказы за последни${days === 1 ? "й" : "е"} ${days} ${dayWord} ${scope}:\n\n`;
  response += `• Всего заказов: ${filtered.length}\n`;
  response += `• Общая сумма: ${formatMoney(totalAmount)}\n`;
  response += `• Общая маржа: ${formatMoney(totalMargin)}\n`;
  response += `• Средняя маржинальность: ${avgMarginPercent.toFixed(1)}%\n\n`;

  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
  sortedDates.forEach((date) => {
    const dayOrders = byDate[date];
    const dayAmount = dayOrders.reduce((s, o) => s + o.amount, 0);
    const dayMargin = dayOrders.reduce((s, o) => s + o.margin, 0);
    const dayMargPct = dayAmount > 0 ? (dayMargin / dayAmount) * 100 : 0;
    response += `📌 ${formatDate(date)} — ${dayOrders.length} заказ(ов)\n`;
    response += `   Сумма: ${formatMoney(dayAmount)} | Маржа: ${formatMoney(dayMargin)} (${dayMargPct.toFixed(1)}%)\n`;
    dayOrders.forEach((o) => {
      const margPct = o.amount > 0 ? (o.margin / o.amount) * 100 : 0;
      response += `   ▸ ${o.id} | ${o.client} | ${formatMoney(o.amount)} | марж. ${margPct.toFixed(0)}%\n`;
    });
    response += `\n`;
  });

  return response;
}

async function handleHelp(): Promise<string> {
  return `🤖 Я — AI-ассистент 1С. Вот что я умею:\n\n` +
    `📊 «Сводка по заказам» — общая статистика\n` +
    `📅 «Заказы за последние N дней» — заказы за период и их маржинальность\n` +
    `🏆 «Топ позиций» — самые продаваемые товары\n` +
    `👥 «Топ клиентов» — лучшие клиенты по выручке\n` +
    `👔 «Топ менеджеров» — статистика по сотрудникам (руководитель)\n` +
    `📦 «Остатки на складе» — складские остатки\n` +
    `🚨 «Просроченные заказы» — заказы без отгрузки в срок\n` +
    `📋 «Заказы менеджера Иванова» — заказы конкретного сотрудника\n` +
    `🏢 «Клиент ТекстильПро» — информация по контрагенту\n` +
    `🧵 «Кому предложить новые ткани» — анализ клиентов для кросс-сейла новых видов тканей\n\n` +
    `Просто напишите вопрос — я найду нужные данные!`;
}

interface FabricClientProfile {
  name: string;
  totalAmount: number;
  totalFabricAmount: number;
  fabricTypes: Set<string>;
  lastOrderDate: string;
  score: number;
}

async function handleFabricRecommendation(ctx: QueryContext): Promise<string> {
  const filtered = filterOrdersByRole(orders, ctx);

  // Все текущие ткани, которые уже продаются
  const existingFabrics = new Set([
    "Ткань х/б арт. ТХ-100",
    "Ткань лён арт. ЛН-200",
    "Ткань шёлк арт. ШЛ-500",
    "Ткань шерсть арт. ШР-300",
    "Ткань джинсовая арт. ДЖ-800",
    "Ткань плащёвая арт. ПЛ-150",
    "Ткань подкладочная арт. ПД-50",
    "Ткань костюмная арт. КС-400",
  ]);

  // Примеры новых видов тканей для рекомендации
  const newFabricExamples = [
    "вискоза (плательная, подкладочная)",
    "трикотаж (кулирка, футер)",
    "органза и фатин",
    "бархат и велюр",
    "неопрен",
    "экокожа",
    "микрофибра",
    "жаккард",
    "атлас и сатин",
  ];

  const clientMap = new Map<string, FabricClientProfile>();

  filtered.forEach((o) => {
    let profile = clientMap.get(o.client);
    if (!profile) {
      profile = {
        name: o.client,
        totalAmount: 0,
        totalFabricAmount: 0,
        fabricTypes: new Set(),
        lastOrderDate: o.date,
        score: 0,
      };
      clientMap.set(o.client, profile);
    }

    profile.totalAmount += o.amount;

    o.items.forEach((item) => {
      if (existingFabrics.has(item.nomenclature)) {
        profile.totalFabricAmount += item.sum;
        profile.fabricTypes.add(item.nomenclature);
      }
    });

    // Обновляем дату последнего заказа
    if (o.date > profile.lastOrderDate) {
      profile.lastOrderDate = o.date;
    }
  });

  // Преобразуем в массив и считаем скоринговые баллы
  const today = new Date("2026-06-22");

  const clients = Array.from(clientMap.values()).map((c) => {
    const fabricTypeCount = c.fabricTypes.size;
    const fabricRatio = c.totalAmount > 0 ? c.totalFabricAmount / c.totalAmount : 0;

    // Давность последнего заказа в днях
    const lastOrder = new Date(c.lastOrderDate);
    const daysSinceLastOrder = Math.floor((today.getTime() - lastOrder.getTime()) / (1000 * 60 * 60 * 24));

    // Скоринг:
    // 1. Разнообразие тканей (0-40 баллов): каждый уникальный тип ткани = 5 баллов, макс 40
    const diversityScore = Math.min(fabricTypeCount * 5, 40);

    // 2. Доля тканей в закупках (0-25 баллов): если клиент в основном покупает ткани, а не фурнитуру
    const fabricFocusScore = Math.round(fabricRatio * 25);

    // 3. Объём закупок тканей (0-25 баллов): чем больше, тем интереснее
    // Берём логарифмическую шкалу: 100K ~ 5, 500K ~ 15, 2M+ ~ 25
    const volumeScore = Math.min(Math.round(Math.log2(c.totalFabricAmount / 50000 + 1) * 5), 25);

    // 4. Давность последнего заказа (0-10 баллов): свежие клиенты теплее
    // 0-7 дней = 10, 7-30 дней = 8, 30-90 дней = 5, 90-180 дней = 2, >180 = 0
    let recencyScore = 0;
    if (daysSinceLastOrder <= 7) recencyScore = 10;
    else if (daysSinceLastOrder <= 30) recencyScore = 8;
    else if (daysSinceLastOrder <= 90) recencyScore = 5;
    else if (daysSinceLastOrder <= 180) recencyScore = 2;

    const totalScore = diversityScore + fabricFocusScore + volumeScore + recencyScore;

    return {
      ...c,
      fabricTypeCount,
      score: totalScore,
    };
  });

  // Сортируем по скору
  const sorted = clients.sort((a, b) => b.score - a.score);

  const scope = ctx.role === "director" ? "по всем менеджерам" : "по Вашим клиентам";

  let response = `🧵 Анализ клиентов для предложения новых видов тканей ${scope}:\n\n`;
  response += `Рекомендация строится на основе:\n`;
  response += `• Разнообразия текущих закупок тканей\n`;
  response += `• Доли тканей в общем объёме\n`;
  response += `• Общего объёма закупок тканей\n`;
  response += `• Давности последнего заказа\n\n`;
  response += `Примеры новых видов тканей, которые можно предложить:\n`;
  newFabricExamples.forEach((f) => {
    response += `  ▸ ${f}\n`;
  });
  response += `\n`;

  response += `📊 Рейтинг клиентов (чем выше балл — тем перспективнее):\n\n`;

  sorted.forEach((c, i) => {
    const tier =
      c.score >= 70 ? "⭐⭐⭐ Приоритет" : c.score >= 50 ? "⭐⭐ Перспективно" : "⭐ Можно прощупать";

    response += `${i + 1}. ${c.name} — ${c.score} баллов ${tier}\n`;
    response += `   Тканей в закупках: ${formatMoney(c.totalFabricAmount)} (${Math.round((c.totalAmount > 0 ? c.totalFabricAmount / c.totalAmount : 0) * 100)}% от оборота)\n`;
    response += `   Видов тканей: ${c.fabricTypeCount} из ${existingFabrics.size}\n`;
    response += `   Последний заказ: ${formatDate(c.lastOrderDate)}\n`;
    response += `\n`;
  });

  response += `💡 Совет: начните с клиентов группы «Приоритет» — они уже покупают разнообразные ткани и открыты к новинкам. Предложите им образцы ${newFabricExamples.slice(0, 3).map((f) => f.split(" (")[0]).join(", ")} с персональной скидкой на пробную партию.`;

  return response;
}

function detectIntent(query: string): string {
  const lower = query.toLowerCase();

  if (lower.includes("помощ") || lower.includes("что ты умееш") || lower.includes("справк") || lower.includes("help")) {
    return "help";
  }
  if (
    (lower.includes("предлож") || lower.includes("рекоменд") || lower.includes("посовет")) &&
    (lower.includes("нов") && (lower.includes("ткан") || lower.includes("вид") || lower.includes("материал")) || lower.includes("кросс-сейл") || lower.includes("кросс сейл"))
  ) {
    return "fabric_recommend";
  }
  if (
    lower.includes("кому") &&
    (lower.includes("ткан") || lower.includes("вид") || lower.includes("материал")) &&
    (lower.includes("предлож") || lower.includes("лучше") || lower.includes("стоит"))
  ) {
    return "fabric_recommend";
  }
  if (lower.includes("сводк") || lower.includes("общ") && lower.includes("статистик") || lower.includes("отчёт") && lower.includes("заказ")) {
    return "summary";
  }
  if (lower.includes("топ позиц") || lower.includes("лучш") && lower.includes("товар") || lower.includes("популярн") || lower.includes("антитоп")) {
    return "top_items";
  }
  if (lower.includes("топ клиент") || lower.includes("лучш") && lower.includes("клиент")) {
    return "top_clients";
  }
  if (lower.includes("менеджер") && (lower.includes("топ") || lower.includes("лучш") || lower.includes("статистик") || lower.includes("рейтинг"))) {
    return "top_managers";
  }
  if (lower.includes("остатк") || lower.includes("склад") || lower.includes("наличие") || lower.includes("запас")) {
    return "inventory";
  }
  if (lower.includes("просроч") || lower.includes("зависш") || lower.includes("не отгружен") || lower.includes("сорван")) {
    return "overdue";
  }
  if (lower.includes("менеджер") && (lower.includes("заказ") || lower.includes("иванов") || lower.includes("петров") || lower.includes("сидоров"))) {
    return "manager_orders";
  }
  if (lower.includes("клиент") || lower.includes("контрагент") || lower.includes("швейная фабрика") || lower.includes("текстильпро") || lower.includes("модный дом") || lower.includes("спецодежда") || lower.includes("домашний текстиль") || lower.includes("швейное объединение") || lower.includes("текспром") || lower.includes("гардинная") || lower.includes("мебельный текстиль") || lower.includes("иголочка")) {
    return "client_info";
  }

  if (lower.includes("последни") && (lower.includes("дн") || lower.includes("день"))) {
    return "recent";
  }

  return "summary";
}

export async function processQuery(
  query: string,
  ctx: QueryContext
): Promise<string> {
  await new Promise((r) => setTimeout(r, 1500 + Math.random() * 2000));

  const intent = detectIntent(query);

  switch (intent) {
    case "help":
      return handleHelp();
    case "summary":
      return handleOrderSummary(ctx);
    case "top_items":
      return handleTopItems(ctx);
    case "top_clients":
      return handleTopClients(ctx);
    case "top_managers":
      return handleTopManagers(ctx);
    case "inventory":
      return handleInventory(query, ctx);
    case "overdue":
      return handleOverdueOrders(ctx);
    case "recent":
      return handleRecentOrders(query, ctx);
    case "manager_orders":
      return handleManagerOrders(query, ctx);
    case "client_info":
      return handleClientInfo(query, ctx);
    case "fabric_recommend":
      return handleFabricRecommendation(ctx);
    default:
      return handleOrderSummary(ctx);
  }
}